import React, { FunctionComponent } from "react";
import Image from "next/image";
import { AssetDetails } from "contentful";

import { IHeroSection, IHeroSectionFields } from "@/types/generated/contentful";
import CallToAction from "../callToAction";
import RichText from "../richtext";

type Props = {
  content: IHeroSection;
};

const HeroSection: FunctionComponent<Props> = ({ content }) => {
  const {
    breadcrumbImage,
    seoCopy,
    heading,
    headingStrongColor,
    body,
    textAlignment,
    imageAlignment,
    callToAction,
    image,
    minHeight,
    background,
    foreground,
    backgroundOverlayImage,
    backgroundOverlayType,
  } = content.fields as IHeroSectionFields;

  const reversed = imageAlignment === "left";

  let textAlign = "text-left items-start";
  switch (textAlignment) {
    case "center":
      textAlign = "text-center items-center";
      break;
    case "right":
      textAlign = "text-right items-end";
      break;
    default:
      textAlign = "text-left items-start";
      break;
  }

  let heroHeight = "";
  switch (minHeight) {
    case "3/4":
      heroHeight = "md:min-h-3/4-screen";
      break;
    case "1/2":
      heroHeight = "md:min-h-1/2-screen";
      break;
    case "1/3":
      heroHeight = "md:min-h-1/3-screen";
      break;
    case "full":
      heroHeight = "md:min-h-screen";
      break;
    default:
      heroHeight = "min-h-fit";
      break;
  }

  return (
    <section
      className={`mx-auto flex w-screen justify-center ${heroHeight} flex-col p-4 pb-8 md:p-8`}
      style={{
        backgroundColor: background || "transparent",
        ...(backgroundOverlayImage?.fields.file?.url && {
          backgroundImage:
            "url('" + backgroundOverlayImage?.fields.file?.url + "')",
        }),
        backgroundSize: backgroundOverlayType,
        ...(backgroundOverlayType === "contain" && {
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }),
        ...(foreground && { color: foreground }),
      }}
    >
      <div className="relative mx-auto grid gap-4 2xl:container md:grid-cols-12 lg:py-4 xl:gap-8">
        <div
          className={`order-2 flex flex-col gap-4 ${breadcrumbImage ? "place-self-center" : "place-self-center"} ${reversed ? "md:order-2" : "md:order-1"} col-span-12 ${image ? "md:col-span-6" : "md:col-span-12"} ${textAlign}`}
        >
          {breadcrumbImage && (
            <Image
              className="hidden max-w-48 md:mb-2 md:block xl:mb-8"
              src={`${breadcrumbImage.fields.file?.url}`}
              width={
                (breadcrumbImage.fields.file?.details as AssetDetails).image
                  ?.width
              }
              height={
                (breadcrumbImage.fields.file?.details as AssetDetails).image
                  ?.height
              }
              alt={breadcrumbImage.fields.description?.toString() || ""}
              unoptimized={true}
              priority={true}
            />
          )}
          {seoCopy && <span className="font-bold">{seoCopy}</span>}
          <h1>
            {
              <RichText
                noContainers={true}
                strongColor={headingStrongColor}
                content={heading}
              />
            }
          </h1>
          {body && (
            <div className={`max-w-2xl font-light md:text-lg lg:text-xl`}>
              <RichText content={body} />
            </div>
          )}
          {callToAction && (
            <div className="flex w-full flex-wrap items-center gap-2 md:flex-nowrap">
              {callToAction.map((action) => (
                <CallToAction content={action} key={action.sys.id} />
              ))}
            </div>
          )}
        </div>
        {image && (
          <div
            className={`order-1 col-span-12 flex items-center justify-center ${reversed ? "md:order-1" : "md:order-2"} md:col-span-6`}
          >
            <Image
              src={`https:${image.fields.file?.url}`}
              alt={image.fields.description as string}
              width={(image.fields.file?.details as AssetDetails).image?.width}
              height={
                (image.fields.file?.details as AssetDetails).image?.height
              }
              className="md:max-w-2xl"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
              priority
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
